import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { useMapState } from "./hooks/useMapState";
import { useMapDevices } from "./hooks/useDevicesByBounds";
import { useMapCollections } from "./hooks/useMapCollections";
import {
  clusterDevices,
  calculatePixelThresholds,
  Cluster,
  MapItem,
} from "./utils/mapUtils";
import DeviceMarker from "./components/DeviceMarker";
import CollectionMarker from "./components/CollectionMarker";
import DeviceCluster from "./components/DeviceCluster";
import StatsDialog from "./components/StatsDialog";
import DSBox from "../system-design/DSBox";
import DSPrimaryButton from "../system-design/DSPrimaryButton";
import DSCircularProgress from "../system-design/DSCircularProgress";
import DSSegmentedTabs from "../system-design/DSSegmentedTabs";
import PageHeader from "../../components/PageHeader";
import { MapBounds, MapDevice, MapCollection } from "../../api/admin";
import DSSecondaryButton from "../system-design/DSSecondaryButton";

type MapMode = "klydos" | "collections";

type Region = {
  name: string;
  center: { lat: number; lng: number };
  zoom: number;
};

const GOOGLE_MAPS_API_KEY = "AIzaSyC5XG3LtScVeWzLYbiYE-JXY4GLFRnGduQ";

const DEFAULT_BOUNDS: MapBounds = {
  northEast: { lat: 49.38, lng: -66.93 },
  southWest: { lat: 24.52, lng: -124.77 },
};

const REGIONS: Region[] = [
  {
    name: "United States",
    center: { lat: 39.113014, lng: -105.358887 },
    zoom: 5,
  },
  { name: "Europe", center: { lat: 48.137154, lng: 11.576124 }, zoom: 5 },
  { name: "Israel", center: { lat: 31.771959, lng: 35.217018 }, zoom: 8 },
];

const OVERLAY_STYLES = {
  loadingIndicator: {
    position: "absolute",
    top: 10,
    right: 10,
    backgroundColor: "background.paper",
    padding: 1,
    paddingX: 1.5,
    borderRadius: 1,
    boxShadow: 2,
    display: "flex",
    alignItems: "center",
    gap: 1,
    zIndex: 1000,
  },
  emptyState: {
    position: "absolute",
    top: 10,
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "paper.elevation8",
    padding: 2,
    borderRadius: 1,
    boxShadow: 2,
    color: "text.secondary",
    fontSize: "14px",
  },
  errorBanner: {
    position: "absolute",
    top: 20,
    left: "50%",
    transform: "translateX(-50%)",
    background: "#d32f2f",
    color: "white",
    padding: 2.5,
    paddingX: 3,
    borderRadius: 2,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    fontSize: "14px",
    fontWeight: 500,
    zIndex: 1001,
    minWidth: "300px",
    textAlign: "center",
  },
  regionButtons: {
    position: "absolute",
    bottom: 16,
    left: 16,
    display: "flex",
    gap: 1,
    flexDirection: "column",
  },
} as const;

type ModeSwitcherProps = {
  mode: MapMode;
  onModeChange: (mode: MapMode) => void;
};

const ModeSwitcher = ({ mode, onModeChange }: ModeSwitcherProps) => (
  <DSSegmentedTabs<MapMode>
    options={[
      { label: "Klydos", value: "klydos" },
      { label: "Collections", value: "collections" },
    ]}
    value={mode}
    onChange={(value) => {
      if (value) onModeChange(value);
    }}
    size="small"
  />
);

const MapLoadingIndicator = ({ isInitialLoad }: { isInitialLoad: boolean }) => (
  <DSBox sx={OVERLAY_STYLES.loadingIndicator}>
    <DSCircularProgress size={16} />
    <DSBox sx={{ fontSize: "12px", color: "text.primary" }}>
      {isInitialLoad ? "Loading..." : "Updating..."}
    </DSBox>
  </DSBox>
);

const MapEmptyState = ({ mode }: { mode: MapMode }) => (
  <DSBox sx={OVERLAY_STYLES.emptyState}>
    No {mode === "klydos" ? "devices" : "collections"} found in this area
  </DSBox>
);

const MapErrorBanner = ({ message }: { message: string }) => (
  <DSBox sx={OVERLAY_STYLES.errorBanner}>
    Error loading devices: {message}
  </DSBox>
);

type RegionButtonsProps = {
  onRegionSelect: (region: Region) => void;
};

const RegionButtons = ({ onRegionSelect }: RegionButtonsProps) => (
  <DSBox sx={OVERLAY_STYLES.regionButtons}>
    {REGIONS.map((region) => (
      <DSPrimaryButton
        key={region.name}
        onClick={() => onRegionSelect(region)}
        size="small"
      >
        {region.name}
      </DSPrimaryButton>
    ))}
  </DSBox>
);

const MapLoadingScreen = () => (
  <DSBox
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <DSCircularProgress />
    <DSBox sx={{ color: "text.secondary" }}>Loading map...</DSBox>
  </DSBox>
);

let googleMapInstance: google.maps.Map | null = null;

const DevicesMap = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticMoveRef = useRef(false);
  const [mapSize, setMapSize] = useState<{ width: number; height: number }>();
  const [debouncedBounds, setDebouncedBounds] =
    useState<MapBounds>(DEFAULT_BOUNDS);
  const [mapMode, setMapMode] = useState<MapMode>("klydos");
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  const { center, zoom, bounds, updateBounds, handleZoomToLocation } =
    useMapState();

  const {
    devices,
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices,
    error: devicesError,
  } = useMapDevices(debouncedBounds);

  const {
    collections,
    isLoading: isLoadingCollections,
    isFetching: isFetchingCollections,
    error: collectionsError,
  } = useMapCollections(debouncedBounds);

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const { isLoading, isFetching, error, mapItems } = useMemo(() => {
    const isKlydosMode = mapMode === "klydos";
    return {
      isLoading: isKlydosMode ? isLoadingDevices : isLoadingCollections,
      isFetching: isKlydosMode ? isFetchingDevices : isFetchingCollections,
      error: isKlydosMode ? devicesError : collectionsError,
      mapItems: isKlydosMode ? devices : collections,
    };
  }, [
    mapMode,
    isLoadingDevices,
    isLoadingCollections,
    isFetchingDevices,
    isFetchingCollections,
    devicesError,
    collectionsError,
    devices,
    collections,
  ]);

  const clusteredItems = useMemo(() => {
    if (!bounds || !mapSize || !mapItems.length) {
      return [];
    }
    const { latPixels, lngPixels } = calculatePixelThresholds(bounds, mapSize);
    return clusterDevices(mapItems as MapItem[], latPixels, lngPixels);
  }, [mapItems, bounds, mapSize]);

  useEffect(() => {
    function handleResize() {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const width = window.innerWidth - rect.left;
        const height = window.innerHeight - rect.top - 1;
        setMapSize({ width, height });
      }
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const extractBoundsData = useCallback(
    (googleBounds: google.maps.LatLngBounds): MapBounds => {
      const ne = googleBounds.getNorthEast();
      const sw = googleBounds.getSouthWest();
      return {
        northEast: { lat: ne.lat(), lng: ne.lng() },
        southWest: { lat: sw.lat(), lng: sw.lng() },
      };
    },
    [],
  );

  const handleIdle = useCallback(() => {
    if (!googleMapInstance) return;
    const newBounds = googleMapInstance.getBounds();
    if (newBounds) {
      setDebouncedBounds(extractBoundsData(newBounds));
    }
  }, [extractBoundsData]);

  const handleBoundsChanged = useCallback(() => {
    if (!googleMapInstance) return;

    const newBounds = googleMapInstance.getBounds();
    updateBounds(newBounds);

    if (!isProgrammaticMoveRef.current) {
    }
    isProgrammaticMoveRef.current = false;
  }, [updateBounds]);

  const handleCenterChanged = useCallback(() => {
    if (!googleMapInstance) return;
    const newCenter = googleMapInstance.getCenter();
    if (newCenter) {
      localStorage.setItem(
        "mapCenter",
        JSON.stringify({ lat: newCenter.lat(), lng: newCenter.lng() }),
      );
    }
  }, []);

  const handleZoomChanged = useCallback(() => {
    if (!googleMapInstance) return;
    const newZoom = googleMapInstance.getZoom();
    if (newZoom !== undefined) {
      localStorage.setItem("mapZoom", String(newZoom));
    }
  }, []);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    googleMapInstance = map;
  }, []);

  const handleClusterClick = useCallback((cluster: Cluster<MapDevice>) => {
    if (!googleMapInstance) return;

    // Mark as programmatic move to prevent "Custom View" override
    isProgrammaticMoveRef.current = true;

    const clusterBounds = new google.maps.LatLngBounds();
    for (const device of cluster.devices) {
      if (device.randomLocation) {
        clusterBounds.extend({
          lat: device.randomLocation.latitude,
          lng: device.randomLocation.longitude,
        });
      }
    }
    googleMapInstance.fitBounds(clusterBounds, 100);
  }, []);

  const handleRegionSelect = useCallback(
    (region: Region) => {
      if (!googleMapInstance) return;
      // Mark as programmatic move to prevent "Custom View" override
      isProgrammaticMoveRef.current = true;

      // Use map instance directly to ensure bounds_changed and idle events fire
      googleMapInstance.setCenter(region.center);
      googleMapInstance.setZoom(region.zoom);
      // Keep React state and localStorage in sync
      handleZoomToLocation(region.center, region.zoom);
    },
    [handleZoomToLocation],
  );

  const handleStatsDialogClose = useCallback(() => {
    setStatsDialogOpen(false);
  }, []);

  const handleStatsDialogOpen = useCallback(() => {
    setStatsDialogOpen(true);
  }, []);

  const showLoadingIndicator = isLoading || isFetching;
  const showEmptyState =
    !isLoading &&
    !isFetching &&
    !error &&
    mapItems.length === 0 &&
    debouncedBounds;

  const renderMarkers = () =>
    clusteredItems.map((item, index) => {
      const isCluster = "devices" in item;

      if (isCluster) {
        if (mapMode === "collections") {
          const cluster = item as Cluster<MapCollection>;
          return cluster.devices.map((collection) => (
            <CollectionMarker
              key={`collection-${collection.id}`}
              collection={collection}
            />
          ));
        }
        return (
          <DeviceCluster
            key={`cluster-${index}`}
            cluster={item as Cluster<MapDevice>}
            onClusterClick={handleClusterClick}
          />
        );
      }

      if (mapMode === "collections") {
        return (
          <CollectionMarker
            key={`collection-${(item as MapCollection).id}`}
            collection={item as MapCollection}
          />
        );
      }
      return (
        <DeviceMarker
          key={`device-${(item as MapDevice).id}`}
          device={item as MapDevice}
        />
      );
    });

  return (
    <DSBox
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
        minHeight: 0,
      }}
    >
      <PageHeader
        overline="Devices"
        title="Real Time Stats"
        subtitle="Live map of clocks and collections playing around the world"
        actions={
          <DSSecondaryButton
            onClick={handleStatsDialogOpen}
            size="small"
            variant="outlined"
          >
            Stats
          </DSSecondaryButton>
        }
        tabs={<ModeSwitcher mode={mapMode} onModeChange={setMapMode} />}
      />

      <DSBox
        ref={containerRef}
        sx={{ position: "relative", flex: 1, minHeight: 0 }}
      >
        {!isMapLoaded && <MapLoadingScreen />}

        {isMapLoaded && mapSize && (
          <GoogleMap
            onLoad={handleMapLoad}
            onBoundsChanged={handleBoundsChanged}
            onIdle={handleIdle}
            onCenterChanged={handleCenterChanged}
            onZoomChanged={handleZoomChanged}
            mapContainerStyle={{
              width: `${mapSize.width}px`,
              height: `${mapSize.height}px`,
            }}
            center={center}
            zoom={zoom}
            options={{ maxZoom: 100 }}
          >
            {showLoadingIndicator && (
              <MapLoadingIndicator isInitialLoad={isLoading} />
            )}
            {showEmptyState && <MapEmptyState mode={mapMode} />}
            {renderMarkers()}
          </GoogleMap>
        )}

        <RegionButtons onRegionSelect={handleRegionSelect} />

        {error && <MapErrorBanner message={error.message} />}
      </DSBox>

      <StatsDialog
        open={statsDialogOpen}
        onClose={handleStatsDialogClose}
        bounds={debouncedBounds}
      />
    </DSBox>
  );
};

export default DevicesMap;
